import { useTranslation } from 'react-i18next';
// frontend/src/components/store/ProductCard.jsx - CORREGIDO CON FEEDBACK
import React, { useState } from 'react';
import {
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Typography,
    Button,
    Chip,
    Box,
    IconButton,
    Tooltip,
    CircularProgress
} from '@mui/material';
import {
    AddShoppingCart as AddToCartIcon,

    Inventory as StockIcon,
    LocalOffer as OfferIcon,
    CheckCircle as CheckIcon
} from '@mui/icons-material';

import { productHelpers, cartHelpers } from '../../services/ecommerceApi';
import { useCart } from '../../hooks/useCart';
import { useNotification } from '../common/GlobalNotifications';

const ProductCard = ({
    product,
    onProductClick,
    showAddToCart = true,

    compact = false
}) => {
  const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [justAdded, setJustAdded] = useState(false);
    const [isFavorite, setIsFavorite] = useState(false);

    const { addToCart, loadCart, openCartDrawer } = useCart();
    const { showSuccess, showError } = useNotification();

    const handleAddToCart = async (e) => {
        e.stopPropagation();

        if (!product.is_in_stock) {
            showError(t('hardcoded.components_store_productcard.producto_no_disponible'));
            return;
        }

        setIsLoading(true);

        try {
            console.log('🛒 Adding to cart:', {
                product_id: product.id,
                quantity: 1
            });

            await addToCart({
                product_id: product.id,
                quantity: 1
            });

            // Recargar el carrito para actualizar el contador
            await loadCart();

            // Mostrar feedback visual
            setJustAdded(true);
            showSuccess(t('hardcoded.components_store_productcard.name_agregado_al_carrito', { name: product.name }));
            openCartDrawer();

            // Quitar el feedback después de 2 segundos
            setTimeout(() => {
                setJustAdded(false);
            }, 2000);

        } catch (error) {
            console.error(t('hardcoded.components_store_productcard.error_adding_to_cart'), error);
            showError(error.message || t('hardcoded.components_store_productcard.error_al_agregar_al_carrito'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleFavoriteClick = (e) => {
        e.stopPropagation();
        setIsFavorite(!isFavorite);
    };

    const handleCardClick = () => {
        if (onProductClick) {
            onProductClick(product);
        }
    };

    const stockStatus = productHelpers.getStockStatus(product);
    const isOnSale = productHelpers.isOnSale(product);
    const discountPercentage = productHelpers.getDiscountPercentage(product);
    const mainImage = productHelpers.getMainImage(product);

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3,
                },
                position: 'relative'
            }}
            onClick={handleCardClick}
        >
            {/* Product Image */}
            <Box sx={{ position: 'relative' }}>
                <CardMedia
                    component="img"
                    height={compact ? 180 : 240}
                    image={mainImage}
                    alt={product.name}
                    sx={{
                        objectFit: 'cover',
                        backgroundColor: '#f5f5f5'
                    }}
                />

                {/* Badges */}
                <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {product.is_featured && (
                        <Chip
                            label={t('hardcoded.components_store_productcard.destacado')}
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 600 }}
                        />
                    )}

                    {isOnSale && (
                        <Chip
                            label={`-${discountPercentage}%`}
                            color="error"
                            size="small"
                            icon={<OfferIcon />}
                            sx={{ fontWeight: 600 }}
                        />
                    )}
                </Box>

                {/* Stock Status */}
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <Chip
                        label={stockStatus.message}
                        color={stockStatus.status === 'in_stock' ? 'success' :
                            stockStatus.status === 'low_stock' ? 'warning' : 'error'}
                        size="small"
                        icon={<StockIcon />}
                    />
                </Box>

                {/* 🆕 Just Added Indicator */}
                {justAdded && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(76, 175, 80, 0.95)',
                            color: 'white',
                            borderRadius: 2,
                            px: 3,
                            py: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            animation: 'fadeInOut 2s ease-in-out'
                        }}
                    >
                        <CheckIcon />
                        <Typography variant="body2" fontWeight={600}>
                            {t('hardcoded.components_store_productcard.agregado')}</Typography>
                    </Box>
                )}
            </Box>

            {/* Product Info */}
            <CardContent sx={{ flexGrow: 1, pb: compact ? 1 : 2 }}>
                <Typography
                    gutterBottom
                    variant={compact ? "subtitle2" : "h6"}
                    component="div"
                    sx={{
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        minHeight: compact ? '2.5em' : '3em'
                    }}
                >
                    {product.name}
                </Typography>

                {!compact && product.short_description && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            mb: 1
                        }}
                    >
                        {product.short_description}
                    </Typography>
                )}

                {/* Pricing */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant={compact ? "subtitle1" : "h6"} color="primary" fontWeight={600}>
                        {cartHelpers.formatCurrency(product.price)}
                    </Typography>

                    {isOnSale && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textDecoration: 'line-through' }}
                        >
                            {cartHelpers.formatCurrency(product.compare_price)}
                        </Typography>
                    )}
                </Box>

                {/* Product Specifications */}
                {!compact && product.specifications && Object.keys(product.specifications).length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        {Object.entries(product.specifications).slice(0, 2).map(([key, value]) => (
                            <Chip
                                key={key}
                                label={`${key}: ${value}`}
                                size="small"
                                variant="outlined"
                                sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }}
                            />
                        ))}
                    </Box>
                )}
            </CardContent>

            {/* Actions */}
            {showAddToCart && (
                <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color={justAdded ? "success" : "primary"}
                        startIcon={justAdded ? <CheckIcon /> : (isLoading ? <CircularProgress size={20} color="inherit" /> : <AddToCartIcon />)}
                        onClick={handleAddToCart}
                        disabled={!product.is_in_stock || isLoading}
                        sx={{
                            fontWeight: 600,
                            textTransform: 'none'
                        }}
                    >
                        {isLoading ? t('hardcoded.components_store_productcard.agregando') :
                            justAdded ? t('hardcoded.components_store_productcard.agregado') :
                                !product.is_in_stock ? t('hardcoded.components_store_productcard.no_disponible') :
                                    t('hardcoded.components_store_productcard.agregar_al_carrito')}
                    </Button>
                </CardActions>
            )}
        </Card>
    );
};

export default ProductCard;